import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";

const objects = [
    { name: "Basket", full: "basket-full.png", empty: "basket-empty.png" },
    { name: "Shopping Cart", full: "cart-full.png", empty: "cart-empty.png" },
    { name: "School Bag", full: "bag-full.png", empty: "bag-empty.png" },
    { name: "Fridge", full: "fridge-full.png", empty: "fridge-empty.png" },
    { name: "Toy Box", full: "toybox-full.png", empty: "toybox-empty.png" },
    { name: "Bookshelf", full: "bookshelf-full.png", empty: "bookshelf-empty.png" },
    { name: "Wallet", full: "wallet-full.png", empty: "wallet-empty.png" },
    { name: "Water Bottle", full: "bottle-full.png", empty: "bottle-empty.png" },
    { name: "Classroom", full: "classroom-full.png", empty: "classroom-empty.png" },
];

const FullEmpty: React.FC = () => {
    const [question, setQuestion] = useState("");
    const [target, setTarget] = useState("long");
    const [selectedObject, setSelectedObject] = useState(objects[0]);
    const [message, setMessage] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateQuestion();
        document.title = "Full and Empty Worksheets for UKG | Pre-Math Concept Activities";
    }, []);

    const generateQuestion = () => {
        const randomObject = objects[Math.floor(Math.random() * objects.length)];
        const randomTarget = Math.random() > 0.5 ? "full" : "empty";

        setSelectedObject(randomObject);
        setTarget(randomTarget);
        setQuestion(`Which one is ${randomTarget}?`);
        setMessage("");
    };

    const checkAnswer = (choice: string) => {
        if (choice === target) {
            setShowImage(true);
            setTimeout(() => {
                generateQuestion()
                setShowImage(false);
                setMessage("")
            }, 5000)
        } else {
            setShowImage(false);
            setMessage("❌ Oops! Try again.");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Help UKG children learn the difference between full and empty using fun visuals and real-life examples. Download printable worksheets for early math development." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/mathematics/pre-math-concepts/full-empty" />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord pickColor matchImage match-container game-container">
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Full and Empty</h1>

                                    <p>
                                        Understanding the difference between <strong>full</strong> and <strong>empty</strong> is one of the earliest lessons in <strong>pre-math concepts</strong>. At the UKG level, it helps children observe volume and quantity using visual cues and real-life examples. With Mama’s Guidance’s <strong>free full and empty worksheets</strong>, learning becomes both fun and effective!
                                    </p>

                                    <h2>🥛 What Do Full and Empty Mean?</h2>
                                    <p>
                                        <strong>Full</strong> means something is filled up or has contents, while <strong>empty</strong> means there is nothing inside.
                                    </p>

                                    <ul>
                                        <li>A glass of milk is <strong>full</strong>. A glass with no milk is <strong>empty</strong>.</li>
                                        <li>A toy box packed with toys is <strong>full</strong>, while one with nothing is <strong>empty</strong>.</li>
                                        <li>A bucket filled with water is <strong>full</strong>. An overturned bucket is <strong>empty</strong>.</li>
                                    </ul>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Visually identify and describe full and empty objects</li>
                                        <li>Understand the concept of quantity and space</li>
                                        <li>Use the terms <strong>“full” and “empty”</strong> accurately</li>
                                        <li>Build logical thinking through observation</li>
                                    </ul>

                                    <h2>🖍️ What’s Inside the Worksheets?</h2>
                                    <ul>
                                        <li>Circle the object that is full or empty</li>
                                        <li>Match items that show full and empty states</li>
                                        <li>Color <strong>full vs. empty</strong> containers</li>
                                        <li>Sort images into <strong>“full” and “empty”</strong> categories</li>
                                        <li>Hands-on activity suggestions for real-world learning</li>
                                    </ul>

                                    <h2>👩‍🏫 Tips for Parents and Teachers</h2>
                                    <ul>
                                        <li>Use water bottles, glasses, or baskets to show full and empty practically</li>
                                        <li>Ask your child: <strong>“Is this full or empty?”</strong> during daily routines</li>
                                        <li>Encourage your child to fill and empty containers while playing</li>
                                        <li>Use storytelling with characters that fill jars, boxes, or bags</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/ukg/mathematics/pre-math-concepts/long-short"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Long - Short</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/pre-math-concepts/heavy-light"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Heavy - Light</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/pre-math-concepts/front-back"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Front - Back</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🌟 Mama’s Guidance Tip</h2>
                                    <p>
                                        Turn snack time into a learning session. Ask, <strong>“Is your plate full or empty?”</strong> or <strong>“Shall we fill your cup again?”</strong> This daily practice builds lasting understanding.
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Full and Empty</h1>

                                <p>
                                    Understanding the difference between <strong>full</strong> and <strong>empty</strong> is one of the earliest lessons in <strong>pre-math concepts</strong>. At the UKG level, it helps children observe volume and quantity using visual cues and real-life examples. With Mama’s Guidance’s <strong>free full and empty worksheets</strong>, learning becomes both fun and effective!
                                </p>

                                <h2>🥛 What Do Full and Empty Mean?</h2>
                                <p>
                                    <strong>Full</strong> means something is filled up or has contents, while <strong>empty</strong> means there is nothing inside.
                                </p>

                                <ul>
                                    <li>A glass of milk is <strong>full</strong>. A glass with no milk is <strong>empty</strong>.</li>
                                    <li>A toy box packed with toys is <strong>full</strong>, while one with nothing is <strong>empty</strong>.</li>
                                    <li>A bucket filled with water is <strong>full</strong>. An overturned bucket is <strong>empty</strong>.</li>
                                </ul>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Visually identify and describe full and empty objects</li>
                                    <li>Understand the concept of quantity and space</li>
                                    <li>Use the terms <strong>“full” and “empty”</strong> accurately</li>
                                    <li>Build logical thinking through observation</li>
                                </ul>

                                <h2>🖍️ What’s Inside the Worksheets?</h2>
                                <ul>
                                    <li>Circle the object that is full or empty</li>
                                    <li>Match items that show full and empty states</li>
                                    <li>Color <strong>full vs. empty</strong> containers</li>
                                    <li>Sort images into <strong>“full” and “empty”</strong> categories</li>
                                    <li>Hands-on activity suggestions for real-world learning</li>
                                </ul>

                                <h2>👩‍🏫 Tips for Parents and Teachers</h2>
                                <ul>
                                    <li>Use water bottles, glasses, or baskets to show full and empty practically</li>
                                    <li>Ask your child: <strong>“Is this full or empty?”</strong> during daily routines</li>
                                    <li>Encourage your child to fill and empty containers while playing</li>
                                    <li>Use storytelling with characters that fill jars, boxes, or bags</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/mathematics/pre-math-concepts/long-short"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Long - Short</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/pre-math-concepts/heavy-light"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Heavy - Light</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/pre-math-concepts/front-back"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Front - Back</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🌟 Mama’s Guidance Tip</h2>
                                <p>
                                    Turn snack time into a learning session. Ask, <strong>“Is your plate full or empty?”</strong> or <strong>“Shall we fill your cup again?”</strong> This daily practice builds lasting understanding.
                                </p>
                            </article>
                            <h2>{question}</h2>
                            <div className="mt-5 mb-5" style={{ display: "flex", justifyContent: "center", gap: "20px" }}>
                                <img src={process.env.REACT_APP_API_URL + selectedObject.full} alt="Full Object" style={{ width: "100px" }} onClick={() => checkAnswer("full")} />
                                <img src={process.env.REACT_APP_API_URL + selectedObject.empty} alt="Empty Object" style={{ width: "100px" }} onClick={() => checkAnswer("empty")} />
                            </div>
                            <p>{message}</p></div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default FullEmpty;
